import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Search,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  User,
  ChevronRight,
  ChevronLeft,
  Bot,
  Phone,
  Loader2,
  AlertCircle,
  X,
  Paperclip,
  FileText,
  Download,
  ChevronDown,
  Eye,
  Image as ImageIcon,
  Star,
  Ban,
  Send,
  Hash,
  UserCheck,
  XCircle,
  SlidersHorizontal,
} from 'lucide-react'
import { conversationsService, Conversation, ConversationMessage, UsageLogEntry, PaginationInfo } from '../../services/conversations.service'
import apiClient from '../../services/api'
import { TaskProgressPanel } from '../../components/conversations/TaskProgressPanel'
import { DocumentsPanel } from '../../components/conversations/DocumentsPanel'
import { taskPipelineService, ConversationAttachmentSummary } from '../../services/taskPipeline.service'
import { channelsService, Channel } from '../../services/channels.service'
import { CustomerTaskProgress } from '../../types'
import { cn } from '../../utils/cn'
import { formatRelativeTime } from '../../utils/format'
import toast from 'react-hot-toast'

type SortKey = 'last_message_at' | 'created_at' | 'status' | 'channel_type' | 'customer_name' | 'total_cost_usd'
type SortDir = 'asc' | 'desc'
type StatusFilterValue = 'all' | 'active' | 'escalated' | 'resolved' | 'waiting' | 'human_active'
type CustomerTagFilter = 'all' | 'high_potential' | 'blocked'

const STATUS_FILTERS: { label: string; value: StatusFilterValue }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Escalated', value: 'escalated' },
]

const CUSTOMER_TAG_FILTERS: { label: string; value: CustomerTagFilter; icon: string }[] = [
  { label: 'High Potential', value: 'high_potential', icon: '⭐' },
  { label: 'Blocked', value: 'blocked', icon: '🚫' },
]

function getStatusDotColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-500'
    case 'escalated':
      return 'bg-red-500'
    case 'resolved':
    case 'closed':
      return 'bg-gray-400'
    case 'waiting':
    case 'waiting_customer':
    case 'waiting_agent':
      return 'bg-amber-500'
    default:
      return 'bg-gray-400'
  }
}

function getStatusBadge(status: string) {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    escalated: 'bg-red-100 text-red-700',
    resolved: 'bg-gray-100 text-gray-700',
    closed: 'bg-gray-100 text-gray-700',
    waiting: 'bg-amber-100 text-amber-700',
    waiting_customer: 'bg-amber-100 text-amber-700',
    waiting_agent: 'bg-amber-100 text-amber-700',
  }
  return styles[status] || 'bg-gray-100 text-gray-700'
}

function getChannelIcon(channel: string): string {
  switch (channel.toLowerCase()) {
    case 'instagram':
      return '📷'
    case 'facebook':
      return '👤'
    case 'whatsapp':
      return '💬'
    case 'telegram':
      return '✈️'
    case 'web':
      return '🌐'
    default:
      return '💬'
  }
}

function getChannelBadge(channel: string): { icon: string; label: string; bg: string; text: string } {
  switch (channel.toLowerCase()) {
    case 'facebook':
      return { icon: '👤', label: 'FB', bg: 'bg-blue-100', text: 'text-blue-700' }
    case 'instagram':
      return { icon: '📷', label: 'IG', bg: 'bg-pink-100', text: 'text-pink-700' }
    case 'telegram':
      return { icon: '✈️', label: 'TG', bg: 'bg-sky-100', text: 'text-sky-700' }
    case 'whatsapp':
      return { icon: '💬', label: 'WA', bg: 'bg-green-100', text: 'text-green-700' }
    case 'web':
    case 'web_widget':
      return { icon: '🌐', label: 'Web', bg: 'bg-gray-100', text: 'text-gray-700' }
    case 'custom':
      return { icon: '⚡', label: 'API', bg: 'bg-indigo-100', text: 'text-indigo-700' }
    default:
      return { icon: '💬', label: channel.slice(0, 3), bg: 'bg-gray-100', text: 'text-gray-600' }
  }
}

export function ConversationsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // List state
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all')
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({ currentPage: 1, perPage: 50, totalItems: 0, totalPages: 0 })

  // Default sort and filter values (can be extended with UI controls later)
  const sortKey: SortKey = 'last_message_at'
  const sortDir: SortDir = 'desc'
  const [channelFilter, setChannelFilter] = useState<string>('all')
  const [customerTagFilter, setCustomerTagFilter] = useState<CustomerTagFilter>('all')
  const [scoreFilter, setScoreFilter] = useState<string>('all')
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [filterDropdownPos, setFilterDropdownPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 })
  const [channels, setChannels] = useState<Channel[]>([])
  const filterRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [escalatedCount, setEscalatedCount] = useState(0)

  // Selected conversation state
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [usageLogs, setUsageLogs] = useState<UsageLogEntry[]>([])
  const [showCostBreakdown, setShowCostBreakdown] = useState(false)

  // Attachments
  const [conversationAttachments, setConversationAttachments] = useState<ConversationAttachmentSummary[]>([])
  const [attachmentPreviews, setAttachmentPreviews] = useState<Record<string, string>>({})
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false)

  // Right panel
  const [showTaskPanel, setShowTaskPanel] = useState(true)
  const [rightPanelTab, setRightPanelTab] = useState<'tasks' | 'documents'>('tasks')
  const [customerProfile, setCustomerProfile] = useState<any>(null)
  const [customerTaskProgress, setCustomerTaskProgress] = useState<CustomerTaskProgress[]>([])
  const [customerAttachments, setCustomerAttachments] = useState<ConversationAttachmentSummary[]>([])

  // Message input
  const [messageInput, setMessageInput] = useState('')
  const [sending, setSending] = useState(false)

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const conversationListRef = useRef<HTMLDivElement>(null)

  // ===== Data fetching =====

  const fetchConversations = useCallback(async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      const result = await conversationsService.getConversations({
        page,
        limit: pagination.perPage,
        status: statusFilter !== 'all' && statusFilter !== 'human_active' ? statusFilter : undefined,
        channel_id: channelFilter !== 'all' ? channelFilter : undefined,
        search: searchTerm || undefined,
        sort_by: sortKey,
        sort_dir: sortDir,
        customer_tag: customerTagFilter !== 'all' ? customerTagFilter : undefined,
        has_human_reply: statusFilter === 'human_active' || undefined,
      })
      setConversations(result.data)
      setPagination(result.pagination)
      // Fetch human-replied count in background
      conversationsService.getConversations({ has_human_reply: true, limit: 1 })
        .then(r => setEscalatedCount(r.pagination.totalItems))
        .catch(() => {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, channelFilter, searchTerm, customerTagFilter, pagination.perPage])

  useEffect(() => {
    fetchConversations(1)
  }, [statusFilter, channelFilter, searchTerm, customerTagFilter])

  // Fetch channels for filter + badge
  useEffect(() => {
    channelsService.getChannels().then(setChannels).catch(() => {})
  }, [])

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setShowFilterDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const channelMap = useMemo(() => {
    const map: Record<string, Channel> = {}
    for (const ch of channels) map[ch.id] = ch
    return map
  }, [channels])

  const displayConversations = useMemo(() => {
    if (scoreFilter === 'all') return conversations
    return conversations.filter(conv => {
      const score = (conv.metadata?.customer_metadata as any)?.lead_score;
      if (scoreFilter === 'unscored') return !score;
      if (scoreFilter === 'hot') return score >= 9;
      if (scoreFilter === 'good') return score >= 7 && score <= 8;
      if (scoreFilter === 'medium') return score >= 5 && score <= 6;
      if (scoreFilter === 'low') return score >= 3 && score <= 4;
      if (scoreFilter === 'minimal') return score >= 1 && score <= 2;
      return true;
    })
  }, [conversations, scoreFilter])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Load conversation if ID is in URL
  useEffect(() => {
    if (id && conversations.length > 0) {
      const conv = conversations.find(c => c.id === id)
      if (conv) {
        handleSelectConversation(conv)
      }
    }
  }, [id, conversations])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Load media previews for conversation attachments (both attachments table and extractions)
  useEffect(() => {
    const loadPreviews = async () => {
      const previews: Record<string, string> = {}
      const token = localStorage.getItem('access_token')
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'

      for (const att of conversationAttachments) {
        const isMediaType = att.mime_type?.startsWith('image/') || att.mime_type?.startsWith('audio/') || att.mime_type?.startsWith('video/') || att.filename?.match(/\.(mp4|mp3|wav|ogg|jpg|jpeg|png|gif|webp)$/i)
        if (!isMediaType) continue

        const isFromExtraction = (att.metadata as any)?.source === 'extraction'
        try {
          if (isFromExtraction && selectedConversation) {
            // Fetch from media endpoint with auth header
            const res = await fetch(`${apiBase}/conversations/${selectedConversation.id}/media/${att.id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            })
            if (res.ok) {
              const blob = await res.blob()
              previews[att.id] = URL.createObjectURL(blob)
            }
          } else if (att.thumbnail_base64) {
            previews[att.id] = `data:${att.mime_type};base64,${att.thumbnail_base64}`
          } else {
            const full = await taskPipelineService.getAttachment(att.id)
            previews[att.id] = `data:${att.mime_type};base64,${full.content_base64}`
          }
        } catch {
          // skip failed preview loads
        }
      }
      if (Object.keys(previews).length > 0) {
        setAttachmentPreviews(previews)
      }
    }
    if (conversationAttachments.length > 0) {
      loadPreviews()
    }
  }, [conversationAttachments, selectedConversation])

  // Keyboard navigation for conversation list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

      e.preventDefault()
      const currentIdx = selectedConversation
        ? conversations.findIndex(c => c.id === selectedConversation.id)
        : -1

      let nextIdx: number
      if (e.key === 'ArrowDown') {
        nextIdx = currentIdx < conversations.length - 1 ? currentIdx + 1 : currentIdx
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : 0
      }

      if (nextIdx >= 0 && nextIdx < conversations.length && nextIdx !== currentIdx) {
        handleSelectConversation(conversations[nextIdx])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [conversations, selectedConversation])

  // ===== Handlers =====

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation)
    navigate(`/conversations/${conversation.id}`)
    setMessagesLoading(true)
    setConversationAttachments([])
    setAttachmentPreviews({})
    setAttachmentsExpanded(false)
    setUsageLogs([])
    setShowCostBreakdown(false)
    try {
      const [msgs, logs, atts, extractions] = await Promise.all([
        conversationsService.getMessages(conversation.id),
        conversationsService.getConversationUsageLogs(conversation.id).catch(() => []),
        taskPipelineService.getAttachmentsForConversation(conversation.id).catch(() => []),
        conversationsService.getExtractions(conversation.id).catch(() => []),
      ])
      setMessages(msgs)
      setUsageLogs(logs)
      // Merge document extractions into attachments so media renders inline
      const extractionAtts = (extractions || [])
        .filter((e: any) => e.r2_key && e.filename)
        .map((e: any) => ({
          id: e.id,
          conversation_id: conversation.id,
          customer_id: e.customer_id || '',
          organization_id: e.organization_id || '',
          filename: e.filename,
          mime_type: e.mime_type || 'application/octet-stream',
          file_size: e.file_size || 0,
          metadata: { r2_key: e.r2_key, extraction_status: e.extraction_status, source: 'extraction' },
          created_at: e.created_at,
          updated_at: e.updated_at,
        }))
      setConversationAttachments([...atts, ...extractionAtts])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages')
    } finally {
      setMessagesLoading(false)
    }
  }

  const isEscalated = selectedConversation?.status === 'escalated'

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || sending) return
    const content = messageInput.trim()
    setMessageInput('')
    setSending(true)

    if (!isEscalated) {
      toast.error('Take over the conversation first to reply as agent')
      setSending(false)
      setMessageInput(content)
      return
    }

    // Human agent takeover — send as human message
    const tempHumanMsg: ConversationMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: selectedConversation.id,
      role: 'assistant',
      sender_type: 'human',
      sender_name: 'You',
      content,
      metadata: {},
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, tempHumanMsg])

    try {
      await conversationsService.sendHumanMessage(selectedConversation.id, content)
    } catch {
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleViewCustomer = async (e: React.MouseEvent, customerId: string) => {
    e.stopPropagation()
    try {
      const [profileRes, taskProgress, custAttachments] = await Promise.all([
        apiClient.get(`/customers/${customerId}/profile`),
        taskPipelineService.getProgressForCustomer(customerId).catch(() => []),
        taskPipelineService.getAttachmentsForCustomer(customerId).catch(() => []),
      ])
      setCustomerProfile(profileRes.data.data)
      setCustomerTaskProgress(taskProgress)
      setCustomerAttachments(custAttachments)
    } catch {
      // error handled by interceptor
    }
  }

  const handleAttachmentDownload = async (attId: string, filename: string) => {
    try {
      const full = await taskPipelineService.getAttachment(attId)
      const byteChars = atob(full.content_base64)
      const byteNumbers = new Uint8Array(byteChars.length)
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i)
      }
      const blob = new Blob([byteNumbers], { type: full.mime_type })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 5000)
      toast.success('Download started')
    } catch {
      toast.error('Failed to download file')
    }
  }

  const handleCustomerFileAction = async (att: ConversationAttachmentSummary, action: 'view' | 'download') => {
    try {
      const full = await taskPipelineService.getAttachment(att.id)
      const byteChars = atob(full.content_base64)
      const bytes = new Uint8Array(byteChars.length)
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
      const blob = new Blob([bytes], { type: full.mime_type })
      const url = URL.createObjectURL(blob)

      if (action === 'view' && att.mime_type.startsWith('image/')) {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = att.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } catch {
      toast.error('Failed to load file')
    }
  }

  const handleCloseConversation = async () => {
    if (!selectedConversation) return
    const isResolved = selectedConversation.status === 'resolved' || (selectedConversation.status as string) === 'closed'
    const newStatus = isResolved ? 'active' : 'resolved'
    try {
      await conversationsService.updateStatus(selectedConversation.id, newStatus)
      setSelectedConversation(prev => prev ? { ...prev, status: newStatus as any } : null)
      fetchConversations(pagination.currentPage)
      toast.success(isResolved ? 'Conversation reopened' : 'Conversation closed')
    } catch {
      toast.error(isResolved ? 'Failed to reopen conversation' : 'Failed to close conversation')
    }
  }

  const handleTakeOver = async () => {
    if (!selectedConversation) return
    const isEscalated = selectedConversation.status === 'escalated'
    const newStatus = isEscalated ? 'active' : 'escalated'
    try {
      await conversationsService.updateStatus(selectedConversation.id, newStatus)
      setSelectedConversation(prev => prev ? { ...prev, status: newStatus as any } : null)
      fetchConversations(pagination.currentPage)
      toast.success(isEscalated ? 'Handed back to AI' : 'Conversation taken over')
    } catch {
      toast.error(isEscalated ? 'Failed to hand back' : 'Failed to take over')
    }
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.totalPages) return
    fetchConversations(page)
  }

  // ===== Cost helpers =====

  const getMessageCost = (message: ConversationMessage): { cost_usd: number; input_tokens: number; output_tokens: number; ops: string[] } | null => {
    if (message.role !== 'assistant' || usageLogs.length === 0) return null
    const msgTime = new Date(message.created_at).getTime()
    const matched = usageLogs.filter(log => Math.abs(new Date(log.created_at).getTime() - msgTime) < 30000)
    if (matched.length === 0) return null
    return {
      cost_usd: matched.reduce((s, l) => s + l.cost_usd, 0),
      input_tokens: matched.reduce((s, l) => s + l.input_tokens, 0),
      output_tokens: matched.reduce((s, l) => s + l.output_tokens, 0),
      ops: [...new Set(matched.map(l => l.operation_type))],
    }
  }

  const getAttachmentCost = (message: ConversationMessage): { cost_usd: number; input_tokens: number; output_tokens: number } | null => {
    if (message.role !== 'user' || usageLogs.length === 0) return null
    if (!/\[Attachment:/.test(message.content)) return null
    const msgTime = new Date(message.created_at).getTime()
    // Extraction can take some time — match within 2 minutes after the message
    const matched = usageLogs.filter(log =>
      log.operation_type === 'document_extraction' &&
      new Date(log.created_at).getTime() - msgTime >= -5000 &&
      new Date(log.created_at).getTime() - msgTime < 120000
    )
    if (matched.length === 0) return null
    return {
      cost_usd: matched.reduce((s, l) => s + l.cost_usd, 0),
      input_tokens: matched.reduce((s, l) => s + l.input_tokens, 0),
      output_tokens: matched.reduce((s, l) => s + l.output_tokens, 0),
    }
  }

  const formatCost = (cost: number) => {
    if (cost < 0.001) return `$${cost.toFixed(6)}`
    if (cost < 0.01) return `$${cost.toFixed(4)}`
    return `$${cost.toFixed(2)}`
  }

  const conversationTotalCost = usageLogs.reduce((sum, log) => sum + log.cost_usd, 0)
  const conversationInputTokens = usageLogs.reduce((sum, log) => sum + log.input_tokens, 0)
  const conversationOutputTokens = usageLogs.reduce((sum, log) => sum + log.output_tokens, 0)

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  // ===== Derived values =====

  const customerName = selectedConversation
    ? (() => {
        const raw = selectedConversation.customer_name || (selectedConversation.metadata?.customer_name as string)
        const isTest = raw && (/^[0-9a-f-]{20,}$/i.test(raw) || /^test[-_]chat/i.test(raw) || /^custom\s/i.test(raw) || /^facebook\s\d/i.test(raw) || raw.length > 50)
        if (raw && !isTest) return raw
        return ({ facebook: 'Facebook User', telegram: 'Telegram User', instagram: 'Instagram User', whatsapp: 'WhatsApp User' } as Record<string, string>)[selectedConversation.channel_type] || selectedConversation.customer_phone || selectedConversation.customer_email || 'Customer'
      })()
    : ''
  const agentName = selectedConversation
    ? ((selectedConversation.metadata?.agent_name as string) || 'AI Agent')
    : ''

  // ===== Customer files section for modal =====

  const customerFilesSection = (
    <>
      {customerAttachments.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Customer Files ({customerAttachments.length})</p>
          <div className="space-y-2">
            {customerAttachments.map((att) => (
              <div key={att.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 border-2 border-[#1a1a2e]">
                {att.mime_type.startsWith('image/') ? (
                  <div className="w-10 h-10 bg-white rounded border-2 border-[#1a1a2e] flex items-center justify-center overflow-hidden flex-shrink-0">
                    <ImageIcon className="w-5 h-5 text-rose-400" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-white rounded border-2 border-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                  <p className="text-xs text-gray-400">
                    {(att.file_size / 1024).toFixed(1)} KB
                    {att.field_name && <span className="ml-2 text-purple-500">&rarr; {att.field_name}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {att.mime_type.startsWith('image/') && (
                    <button
                      onClick={() => handleCustomerFileAction(att, 'view')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                  )}
                  <button
                    onClick={() => handleCustomerFileAction(att, 'download')}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )

  // ===== RENDER =====

  return (
    <div className="h-[calc(100vh-6rem)] flex bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">

      {/* ==================== LEFT COLUMN: Conversation List ==================== */}
      <div className="w-80 flex-shrink-0 border-r border-gray-200 flex flex-col bg-white">
        {/* Search */}
        <div className="p-3 border-b-2 border-[#1a1a2e]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearchInput(''); setSearchTerm(''); (e.target as HTMLInputElement).blur() } }}
              className="w-full h-9 pl-9 pr-8 bg-gray-50 border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearchTerm('') }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Filters row: status pills + filter icon */}
        <div className="px-3 py-2 flex items-center gap-1.5 border-b-2 border-[#1a1a2e]">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors',
                statusFilter === f.value
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {f.label}
            </button>
          ))}
          <div className="flex-1" />
          {/* Filter dropdown trigger */}
          <div className="relative flex-shrink-0" ref={filterRef}>
            <button
              onClick={() => {
                if (!showFilterDropdown && filterRef.current) {
                  const rect = filterRef.current.getBoundingClientRect()
                  setFilterDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
                }
                setShowFilterDropdown(prev => !prev)
              }}
              className={cn(
                'p-1.5 rounded-lg transition-colors relative flex-shrink-0',
                (channelFilter !== 'all' || customerTagFilter !== 'all' || statusFilter === 'human_active' || scoreFilter !== 'all')
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              )}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {(channelFilter !== 'all' || customerTagFilter !== 'all' || statusFilter === 'human_active' || scoreFilter !== 'all') && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full" />
              )}
            </button>
            {showFilterDropdown && (
              <div
                className="fixed w-56 bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[3px_3px_0px_#1a1a2e] z-[9999] py-2"
                style={{ top: filterDropdownPos.top, right: filterDropdownPos.right }}
              >
                <div className="px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Channel</span>
                </div>
                <button
                  onClick={() => { setChannelFilter('all'); setShowFilterDropdown(false) }}
                  className={cn('w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50', channelFilter === 'all' && 'font-semibold text-indigo-600')}
                >
                  All Channels
                </button>
                {channels.map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => { setChannelFilter(ch.id); setShowFilterDropdown(false) }}
                    className={cn('w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50', channelFilter === ch.id && 'font-semibold text-indigo-600')}
                  >
                    <span className="text-[10px]">{getChannelIcon(ch.channel_type)}</span>
                    <span className="truncate">{ch.name}</span>
                    <span className="ml-auto text-[10px] text-gray-400 capitalize">{ch.channel_type}</span>
                  </button>
                ))}
                <div className="mx-3 my-1.5 border-t border-gray-100" />
                <div className="px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Intervention</span>
                </div>
                <button
                  onClick={() => { setStatusFilter(statusFilter === 'human_active' ? 'all' : 'human_active'); setShowFilterDropdown(false) }}
                  className={cn('w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50', statusFilter === 'human_active' && 'font-semibold text-emerald-600')}
                >
                  <span className="text-[10px]">👤</span>
                  Human Takeover
                  {escalatedCount > 0 && (
                    <span className="ml-auto text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">{escalatedCount}</span>
                  )}
                </button>
                <div className="mx-3 my-1.5 border-t border-gray-100" />
                <div className="px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Tags</span>
                </div>
                {CUSTOMER_TAG_FILTERS.map(f => (
                  <button
                    key={f.value}
                    onClick={() => { setCustomerTagFilter(prev => prev === f.value ? 'all' : f.value); setShowFilterDropdown(false) }}
                    className={cn('w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50', customerTagFilter === f.value && 'font-semibold text-indigo-600')}
                  >
                    <span className="text-[10px]">{f.icon}</span>
                    {f.label}
                  </button>
                ))}
                <div className="mx-3 my-1.5 border-t border-gray-100" />
                <div className="px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Lead Score</span>
                </div>
                {([
                  { value: 'all', label: 'All Scores' },
                  { value: 'hot', label: '\uD83D\uDFE2 Hot (9-10)' },
                  { value: 'good', label: '\uD83D\uDD35 Good (7-8)' },
                  { value: 'medium', label: '\uD83D\uDFE1 Medium (5-6)' },
                  { value: 'low', label: '\uD83D\uDFE0 Low (3-4)' },
                  { value: 'minimal', label: '\u26AA Minimal (1-2)' },
                  { value: 'unscored', label: '\u2014 Unscored' },
                ] as const).map(f => (
                  <button
                    key={f.value}
                    onClick={() => { setScoreFilter(f.value); setShowFilterDropdown(false) }}
                    className={cn('w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50', scoreFilter === f.value && 'font-semibold text-indigo-600')}
                  >
                    {f.label}
                  </button>
                ))}
                {(channelFilter !== 'all' || customerTagFilter !== 'all' || statusFilter === 'human_active' || scoreFilter !== 'all') && (
                  <>
                    <div className="mx-3 my-1.5 border-t border-gray-100" />
                    <button
                      onClick={() => { setChannelFilter('all'); setCustomerTagFilter('all'); setStatusFilter('all'); setScoreFilter('all'); setShowFilterDropdown(false) }}
                      className="w-full px-3 py-1.5 text-left text-xs text-red-500 hover:bg-red-50 font-medium"
                    >
                      Clear all filters
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Conversation list */}
        <div ref={conversationListRef} className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Loader2 className="w-6 h-6 text-rose-600 animate-spin" />
              <p className="text-xs text-gray-400">Loading...</p>
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-xs text-red-600 mb-2">{error}</p>
              <button
                onClick={() => fetchConversations(pagination.currentPage)}
                className="text-xs text-rose-600 font-medium hover:underline"
              >
                Retry
              </button>
            </div>
          ) : displayConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center px-4">
              <MessageSquare className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500 font-medium">No conversations</p>
              <p className="text-xs text-gray-400">
                {searchTerm || statusFilter !== 'all' || channelFilter !== 'all' || scoreFilter !== 'all' ? 'Try adjusting your filters' : 'Conversations appear here'}
              </p>
            </div>
          ) : (
            <>
              {displayConversations.map((conv) => {
                const rawName = conv.customer_name || (conv.metadata?.customer_name as string)
                const isTestName = rawName && (/^[0-9a-f-]{20,}$/i.test(rawName) || /^test[-_]chat/i.test(rawName) || /^custom\s/i.test(rawName) || /^facebook\s\d/i.test(rawName) || rawName.length > 50)
                const channelFallback: Record<string, string> = { facebook: 'Facebook User', telegram: 'Telegram User', instagram: 'Instagram User', whatsapp: 'WhatsApp User' }
                const name = (rawName && !isTestName) ? rawName : (channelFallback[conv.channel_type] || conv.customer_phone || conv.customer_email || 'Customer')
                const lastMessage = (conv.metadata?.last_message_preview as string) || ''
                const convAgentName = (conv.metadata?.agent_name as string) || ''
                const isSelected = selectedConversation?.id === conv.id
                const isHighPotential = (conv.metadata?.customer_metadata as any)?.is_high_potential
                const isBlocked = (conv.metadata?.customer_metadata as any)?.is_blocked
                const hasError = Boolean(conv.metadata?.last_error)

                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={cn(
                      'px-3 py-3 cursor-pointer border-b-2 border-[#1a1a2e] transition-colors hover:bg-gray-50',
                      isSelected && 'bg-rose-50 border-l-2 border-l-rose-600 hover:bg-rose-50'
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-9 h-9 rounded-full bg-rose-500 border-2 border-[#1a1a2e] flex items-center justify-center text-white text-sm font-semibold">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <span className={cn(
                          'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                          lastMessage ? getStatusDotColor(conv.status) : 'bg-gray-400'
                        )} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                            {(conv.metadata?.customer_metadata as any)?.lead_score && (
                              <span className={cn(
                                'ml-0.5 inline-flex items-center justify-center w-5 h-5 rounded-md text-[9px] font-black text-white flex-shrink-0',
                                (conv.metadata.customer_metadata as any).lead_score >= 9 ? 'bg-emerald-500' :
                                (conv.metadata.customer_metadata as any).lead_score >= 7 ? 'bg-blue-500' :
                                (conv.metadata.customer_metadata as any).lead_score >= 5 ? 'bg-amber-400' :
                                (conv.metadata.customer_metadata as any).lead_score >= 3 ? 'bg-orange-400' : 'bg-gray-400'
                              )}>
                                {(conv.metadata.customer_metadata as any).lead_score}
                              </span>
                            )}
                            {isHighPotential && <Star className="w-3 h-3 text-green-600 flex-shrink-0" />}
                            {isBlocked && <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />}
                            {hasError && <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                            {formatRelativeTime(conv.last_message_at)}
                          </span>
                        </div>

                        <p className="text-xs text-gray-500 truncate mt-0.5 leading-relaxed">
                          {lastMessage || 'No messages yet'}
                        </p>

                        <div className="flex items-center gap-1.5 mt-1">
                          {(() => {
                            const ch = channelMap[(conv.metadata as any)?.channel_id]
                            const badge = getChannelBadge(ch?.channel_type || conv.channel_type)
                            return (
                              <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border border-[#1a1a2e]/10', badge.bg, badge.text)}>
                                <span>{badge.icon}</span>
                                {ch?.name || badge.label}
                              </span>
                            )
                          })()}
                          {convAgentName && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200 truncate max-w-[120px]">
                              <Bot className="w-3 h-3 flex-shrink-0" />
                              {convAgentName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50/50">
                  <span className="text-[11px] text-gray-400">
                    {pagination.totalItems} total
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => goToPage(pagination.currentPage - 1)}
                      disabled={pagination.currentPage <= 1}
                      className="p-1 rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-[11px] text-gray-500 mx-1">
                      {pagination.currentPage} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => goToPage(pagination.currentPage + 1)}
                      disabled={pagination.currentPage >= pagination.totalPages}
                      className="p-1 rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ==================== CENTER COLUMN: Chat ==================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedConversation ? (
          // Empty state
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a conversation</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Choose a conversation from the list to view messages and details.
              Use arrow keys to navigate.
            </p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#1a1a2e] bg-white flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-rose-500 border-2 border-[#1a1a2e] flex items-center justify-center text-white text-sm font-semibold">
                    {customerName.charAt(0).toUpperCase()}
                  </div>
                  <span className={cn(
                    'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                    getStatusDotColor(selectedConversation.status)
                  )} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3
                      className="text-sm font-semibold text-gray-900 truncate hover:text-rose-600 cursor-pointer"
                      onClick={(e) => handleViewCustomer(e, selectedConversation.customer_id)}
                    >
                      {customerName}
                    </h3>
                    <span className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize',
                      getStatusBadge(selectedConversation.status)
                    )}>
                      {selectedConversation.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200">
                      <Bot className="w-3 h-3 flex-shrink-0" />
                      {agentName}
                    </span>
                    <span className="text-xs text-gray-400">via</span>
                    <span className="text-xs text-gray-500 capitalize">{selectedConversation.channel_type}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Quick tag buttons */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation()
                    const custMeta = (selectedConversation.metadata?.customer_metadata as any) || {}
                    const isHighPotential = !custMeta.is_high_potential
                    try {
                      await conversationsService.updateCustomerTags(selectedConversation.customer_id, { is_high_potential: isHighPotential })
                      setSelectedConversation(prev => prev ? {
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          customer_metadata: { ...custMeta, is_high_potential: isHighPotential, high_potential_tagged_at: isHighPotential ? new Date().toISOString() : null },
                        },
                      } : null)
                      fetchConversations(pagination.currentPage)
                      toast.success(isHighPotential ? 'Marked as high potential' : 'High potential tag removed')
                    } catch { toast.error('Failed to update tag') }
                  }}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors',
                    (selectedConversation.metadata?.customer_metadata as any)?.is_high_potential
                      ? 'bg-green-100 text-green-700'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-green-600'
                  )}
                  title="Toggle high potential"
                >
                  <Star className="w-4 h-4" />
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation()
                    const custMeta = (selectedConversation.metadata?.customer_metadata as any) || {}
                    const isBlocked = !custMeta.is_blocked
                    try {
                      await conversationsService.updateCustomerTags(selectedConversation.customer_id, { is_blocked: isBlocked })
                      setSelectedConversation(prev => prev ? {
                        ...prev,
                        metadata: {
                          ...prev.metadata,
                          customer_metadata: { ...custMeta, is_blocked: isBlocked, blocked_at: isBlocked ? new Date().toISOString() : null },
                        },
                      } : null)
                      fetchConversations(pagination.currentPage)
                      toast.success(isBlocked ? 'Customer blocked' : 'Customer unblocked')
                    } catch { toast.error('Failed to update tag') }
                  }}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors',
                    (selectedConversation.metadata?.customer_metadata as any)?.is_blocked
                      ? 'bg-red-100 text-red-700'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-red-600'
                  )}
                  title="Toggle block"
                >
                  <Ban className="w-4 h-4" />
                </button>

                {conversationTotalCost > 0 && (
                  <button
                    onClick={() => setShowCostBreakdown(!showCostBreakdown)}
                    className={cn(
                      'px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors',
                      showCostBreakdown
                        ? 'bg-purple-100 text-purple-800 border-purple-300'
                        : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                    )}
                  >
                    {formatCost(conversationTotalCost)}
                  </button>
                )}

                <button
                  onClick={() => setShowTaskPanel(!showTaskPanel)}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors',
                    showTaskPanel ? 'bg-rose-100 text-rose-600' : 'text-gray-400 hover:bg-gray-100'
                  )}
                  title="Toggle task pipeline"
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Cost breakdown panel */}
            {showCostBreakdown && conversationTotalCost > 0 && (() => {
              const opBreakdown = usageLogs.reduce((acc, log) => {
                const key = log.operation_type
                if (!acc[key]) acc[key] = { count: 0, cost: 0, input: 0, output: 0 }
                acc[key].count++
                acc[key].cost += log.cost_usd
                acc[key].input += log.input_tokens
                acc[key].output += log.output_tokens
                return acc
              }, {} as Record<string, { count: number; cost: number; input: number; output: number }>)

              const opLabels: Record<string, { label: string; color: string }> = {
                chat_completion: { label: 'Chat', color: 'bg-blue-100 text-blue-800' },
                query_intelligence: { label: 'Query Intel', color: 'bg-amber-100 text-amber-800' },
                field_extraction: { label: 'Field Extract', color: 'bg-green-100 text-green-800' },
                document_generation: { label: 'Doc Gen', color: 'bg-indigo-100 text-indigo-800' },
                embedding: { label: 'Embedding', color: 'bg-gray-100 text-gray-800' },
              }

              return (
                <div className="px-4 py-2 bg-purple-50 border-b-2 border-[#1a1a2e] flex-shrink-0">
                  <div className="flex items-center gap-4 text-xs">
                    <div>
                      <span className="text-purple-500 uppercase tracking-wider font-semibold text-[10px]">Total</span>
                      <p className="text-purple-900 font-bold text-sm">{formatCost(conversationTotalCost)}</p>
                    </div>
                    <div className="w-px h-6 bg-purple-200" />
                    <div>
                      <span className="text-purple-500 uppercase tracking-wider font-semibold text-[10px]">In</span>
                      <p className="text-purple-900 font-bold text-xs">{conversationInputTokens.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-purple-500 uppercase tracking-wider font-semibold text-[10px]">Out</span>
                      <p className="text-purple-900 font-bold text-xs">{conversationOutputTokens.toLocaleString()}</p>
                    </div>
                    <div className="w-px h-6 bg-purple-200" />
                    <div>
                      <span className="text-purple-500 uppercase tracking-wider font-semibold text-[10px]">Requests</span>
                      <p className="text-purple-900 font-bold text-xs">{usageLogs.length}</p>
                    </div>
                  </div>
                  {Object.keys(opBreakdown).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {Object.entries(opBreakdown).sort((a, b) => b[1].cost - a[1].cost).map(([opType, data]) => {
                        const config = opLabels[opType] || { label: opType.replace(/_/g, ' '), color: 'bg-gray-100 text-gray-700' }
                        return (
                          <div key={opType} className={cn('px-2 py-0.5 rounded text-[10px] font-medium', config.color)} title={`${data.input.toLocaleString()} in / ${data.output.toLocaleString()} out`}>
                            {config.label}: {formatCost(data.cost)} ({data.count})
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Error banner */}
            {Boolean(selectedConversation.metadata?.last_error) && (
              <div className="mx-3 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2 flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-red-700">{String(selectedConversation.metadata.last_error)}</span>
                  {Boolean(selectedConversation.metadata.last_error_at) && (
                    <span className="text-[10px] text-red-400 ml-2">{new Date(String(selectedConversation.metadata.last_error_at)).toLocaleString()}</span>
                  )}
                </div>
                <button
                  onClick={async () => {
                    try {
                      await conversationsService.updateConversation(selectedConversation.id, {
                        metadata: { ...selectedConversation.metadata, last_error: null, last_error_at: null },
                      })
                      setSelectedConversation(prev => prev ? { ...prev, metadata: { ...prev.metadata, last_error: undefined, last_error_at: undefined } } : null)
                      toast.success('Error dismissed')
                    } catch (_) { /* ignore */ }
                  }}
                  className="text-[10px] font-medium text-red-500 hover:text-red-700 px-1.5 py-0.5 rounded hover:bg-red-100 flex-shrink-0"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No messages yet</p>
                  <p className="text-xs text-gray-400">Start the conversation by sending a message</p>
                </div>
              ) : (
                <>
                  {messages
                    .filter((message) => !(message.role === 'system' && message.metadata?.hidden === true))
                    .map((message) => {
                    const isCustomer = message.sender_type === 'customer' || (!message.sender_type && message.role === 'user')
                    const isHuman = message.sender_type === 'human'
                    const isOurSide = !isCustomer && message.role !== 'system'

                    // Parse attachment patterns from message content
                    const attachmentRegex = /\[Attachment:\s*\w+\s*-\s*([^\]]+)\]/g
                    const attachmentMatches = [...message.content.matchAll(attachmentRegex)]
                    const hasInlineAttachments = isCustomer && attachmentMatches.length > 0
                    const textContent = hasInlineAttachments
                      ? message.content.replace(attachmentRegex, '').trim()
                      : message.content

                    // Find matching attachments from conversation attachments
                    const matchedAttachments = hasInlineAttachments
                      ? attachmentMatches.map(match => {
                          const filename = match[1].trim()
                          return conversationAttachments.find(a => a.filename === filename) || { filename, id: '', mime_type: '', file_size: 0 }
                        })
                      : []

                    return (
                    <div
                      key={message.id}
                      className={cn(
                        'flex',
                        message.role === 'system' ? 'justify-center' : isCustomer ? 'justify-start' : 'justify-end'
                      )}
                    >
                      {message.role === 'system' ? (
                        <div className="px-3 py-1.5 bg-gray-200 rounded-full text-xs text-gray-500">
                          {message.content}
                        </div>
                      ) : (
                        <div className={cn(
                          'flex items-end gap-2 max-w-[70%]',
                          isCustomer ? 'flex-row' : 'flex-row-reverse'
                        )}>
                          <div className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                            isCustomer ? 'bg-gray-200' : isHuman ? 'bg-emerald-500' : 'bg-rose-500'
                          )}>
                            {isCustomer ? (
                              <User className="w-3.5 h-3.5 text-gray-600" />
                            ) : isHuman ? (
                              <UserCheck className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <Bot className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                          <div>
                            {/* Attribution label */}
                            {isOurSide && (
                              <p className={cn(
                                'text-[10px] font-medium mb-0.5 text-right',
                                isHuman ? 'text-emerald-600' : message.agent_name ? 'text-violet-500' : 'text-gray-400'
                              )}>
                                {isHuman ? (message.sender_name || 'Agent') : (message.agent_name || 'AI')}
                              </p>
                            )}
                            <div className={cn(
                              'px-3.5 py-2.5 rounded-xl text-sm leading-relaxed',
                              isCustomer
                                ? 'bg-white text-gray-900 rounded-bl-sm border-2 border-[#1a1a2e]'
                                : isHuman
                                  ? 'bg-emerald-500 text-white rounded-br-sm'
                                  : 'bg-rose-500 text-white rounded-br-sm'
                            )}>
                              {textContent && <p className="whitespace-pre-wrap">{textContent}</p>}
                              {/* Inline media — messenger style */}
                              {matchedAttachments.length > 0 && (
                                <div className={cn('space-y-1.5', textContent && 'mt-2')}>
                                  {matchedAttachments.map((att, idx) => {
                                    const isRealAttachment = att.id && att.id !== ''
                                    const isImage = att.mime_type?.startsWith('image/')
                                    const isAudio = att.mime_type?.startsWith('audio/') || (att.filename?.startsWith('audio_') && att.mime_type?.includes('mp4'))
                                    const isVideo = att.mime_type?.startsWith('video/') || (att.filename?.startsWith('video_') && att.mime_type?.includes('mp4'))
                                    const previewUrl = attachmentPreviews[att.id]

                                    // Inline image — messenger style
                                    if (isRealAttachment && isImage && previewUrl) {
                                      return (
                                        <div key={idx} className="rounded-lg overflow-hidden border border-gray-200 max-w-[240px] cursor-pointer" onClick={() => window.open(previewUrl, '_blank')}>
                                          <img src={previewUrl} alt={att.filename} className="w-full h-auto max-h-[200px] object-cover" />
                                        </div>
                                      )
                                    }

                                    // Inline audio player
                                    if (isRealAttachment && isAudio && previewUrl) {
                                      return (
                                        <div key={idx} className="rounded-lg border border-gray-200 p-2 bg-gray-50 max-w-[280px]">
                                          <audio controls preload="metadata" className="w-full h-8" style={{ minWidth: '200px' }}>
                                            <source src={previewUrl} type="audio/mp4" />
                                          </audio>
                                          <p className="text-[10px] text-gray-400 mt-1 truncate">{att.filename}</p>
                                        </div>
                                      )
                                    }

                                    // Inline video player
                                    if (isRealAttachment && isVideo && previewUrl) {
                                      return (
                                        <div key={idx} className="rounded-lg overflow-hidden border border-gray-200 max-w-[280px]">
                                          <video controls preload="metadata" className="w-full max-h-[200px]">
                                            <source src={previewUrl} type="video/mp4" />
                                          </video>
                                          <p className="text-[10px] text-gray-400 p-1 truncate">{att.filename}</p>
                                        </div>
                                      )
                                    }

                                    // Fallback: file card with icon
                                    return (
                                      <div
                                        key={idx}
                                        className={cn(
                                          'flex items-center gap-2 rounded-lg p-2 border',
                                          isOurSide ? 'bg-white/20 border-white/30' : 'bg-gray-50 border-gray-200'
                                        )}
                                      >
                                        {isImage ? (
                                          <ImageIcon className={cn('w-4 h-4 flex-shrink-0', isOurSide ? 'text-rose-100' : 'text-rose-500')} />
                                        ) : (
                                          <FileText className={cn('w-4 h-4 flex-shrink-0', isOurSide ? 'text-rose-100' : 'text-gray-500')} />
                                        )}
                                        <span className={cn('text-xs font-medium truncate flex-1', isOurSide ? 'text-rose-50' : 'text-gray-700')}>
                                          {att.filename}
                                        </span>
                                        {isRealAttachment && (
                                          <div className="flex items-center gap-0.5 flex-shrink-0">
                                            {isImage && (
                                              <button
                                                onClick={async (e) => {
                                                  e.stopPropagation()
                                                  try {
                                                    const full = await taskPipelineService.getAttachment(att.id)
                                                    const byteChars = atob(full.content_base64)
                                                    const bytes = new Uint8Array(byteChars.length)
                                                    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
                                                    const blob = new Blob([bytes], { type: full.mime_type })
                                                    window.open(URL.createObjectURL(blob), '_blank')
                                                  } catch { toast.error('Failed to view file') }
                                                }}
                                                className={cn('p-1 rounded transition-colors', isOurSide ? 'text-rose-100 hover:bg-rose-400/40' : 'text-rose-500 hover:bg-rose-50')}
                                                title="View"
                                              >
                                                <Eye className="w-3 h-3" />
                                              </button>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                handleAttachmentDownload(att.id, att.filename)
                                              }}
                                              className={cn('p-1 rounded transition-colors', isOurSide ? 'text-rose-100 hover:bg-rose-400/40' : 'text-purple-600 hover:bg-purple-50')}
                                              title="Download"
                                            >
                                              <Download className="w-3 h-3" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                            <div className={cn(
                              'flex items-center gap-2 mt-0.5',
                              isOurSide ? 'justify-end' : 'justify-start'
                            )}>
                              <p className="text-[11px] text-gray-400">
                                {formatMessageTime(message.created_at)}
                              </p>
                              {message.role === 'assistant' && (() => {
                                const cost = getMessageCost(message)
                                if (!cost) return null
                                return (
                                  <span className="text-[11px] text-purple-400" title={cost.ops.join(', ')}>
                                    {formatCost(cost.cost_usd)}
                                  </span>
                                )
                              })()}
                              {isCustomer && (() => {
                                const cost = getAttachmentCost(message)
                                if (!cost) return null
                                return (
                                  <span className="text-[11px] text-purple-400" title={`Extraction: ${cost.input_tokens.toLocaleString()} in / ${cost.output_tokens.toLocaleString()} out`}>
                                    {formatCost(cost.cost_usd)}
                                  </span>
                                )
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    )
                  })}

                  {/* Attachments bar */}
                  {conversationAttachments.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => setAttachmentsExpanded(!attachmentsExpanded)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-3.5 h-3.5 text-purple-600" />
                          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
                            Files ({conversationAttachments.length})
                          </span>
                        </div>
                        <ChevronDown className={cn('w-3.5 h-3.5 text-purple-500 transition-transform', attachmentsExpanded && 'rotate-180')} />
                      </button>
                      {attachmentsExpanded && (
                        <div className="mt-2 bg-white border-2 border-[#1a1a2e] rounded-xl p-2">
                          <div className="flex flex-wrap gap-2">
                            {conversationAttachments.map((att) => (
                              <div key={att.id} className="bg-gray-50 rounded-lg border-2 border-[#1a1a2e] p-2 flex items-center gap-2 min-w-[180px] max-w-[240px] hover:border-purple-300 transition-colors">
                                {att.mime_type.startsWith('image/') ? (
                                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {attachmentPreviews[att.id] ? (
                                      <img src={attachmentPreviews[att.id]} alt={att.filename} className="w-full h-full object-cover rounded" />
                                    ) : (
                                      <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />
                                    )}
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-700 truncate">{att.filename}</p>
                                  <p className="text-[10px] text-gray-400">
                                    {att.file_size < 1024 ? `${att.file_size} B` : att.file_size < 1048576 ? `${(att.file_size / 1024).toFixed(1)} KB` : `${(att.file_size / 1048576).toFixed(1)} MB`}
                                  </p>
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  {att.mime_type.startsWith('image/') && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const full = await taskPipelineService.getAttachment(att.id)
                                          const byteChars = atob(full.content_base64)
                                          const bytes = new Uint8Array(byteChars.length)
                                          for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
                                          const blob = new Blob([bytes], { type: full.mime_type })
                                          window.open(URL.createObjectURL(blob), '_blank')
                                        } catch { /* silent */ }
                                      }}
                                      className="p-1 rounded bg-rose-50 text-rose-600 hover:bg-rose-100"
                                      title="View"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleAttachmentDownload(att.id, att.filename)}
                                    className="p-1 rounded bg-purple-50 text-purple-600 hover:bg-purple-100"
                                    title="Download"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message input */}
            <div className="border-t border-gray-200 bg-white p-3 flex-shrink-0">
              {isEscalated ? (
                <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">
                    Replying as agent — messages sent directly to customer
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">
                    Take over this conversation to reply as agent
                  </span>
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  placeholder={isEscalated ? "Reply as agent..." : "Type a message..."}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border-2 border-[#1a1a2e] px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none max-h-32"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || sending}
                  className={cn(
                    'p-2.5 rounded-xl transition-colors flex-shrink-0',
                    messageInput.trim() && !sending
                      ? isEscalated ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-rose-500 text-white hover:bg-rose-600'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ==================== RIGHT COLUMN: Details Panel ==================== */}
      {selectedConversation && showTaskPanel && (
        <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto hidden lg:flex lg:flex-col">
          {/* Customer info */}
          <div className="p-4 border-b-2 border-[#1a1a2e]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-500 border-2 border-[#1a1a2e] flex items-center justify-center text-white font-semibold">
                {customerName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p
                  className="text-sm font-semibold text-gray-900 truncate hover:text-rose-600 cursor-pointer"
                  onClick={(e) => handleViewCustomer(e, selectedConversation.customer_id)}
                >
                  {customerName}
                </p>
                {selectedConversation.customer_email && (
                  <p className="text-xs text-gray-500 truncate">{selectedConversation.customer_email}</p>
                )}
              </div>
            </div>
            {selectedConversation.customer_phone && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                <span>{selectedConversation.customer_phone}</span>
              </div>
            )}
            {selectedConversation.customer_external_id && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Hash className="w-3.5 h-3.5 text-gray-400" />
                <span className="truncate">{selectedConversation.customer_external_id}</span>
              </div>
            )}
          </div>

          {/* Conversation metadata */}
          <div className="p-4 border-b-2 border-[#1a1a2e] space-y-2.5">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Conversation</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <span className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize',
                  getStatusBadge(selectedConversation.status)
                )}>
                  {selectedConversation.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Channel</span>
                <span className="text-xs text-gray-900 capitalize flex items-center gap-1">
                  {getChannelIcon(selectedConversation.channel_type)} {selectedConversation.channel_type}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Agent</span>
                <span className="text-xs text-gray-900 truncate max-w-[120px]">{agentName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Created</span>
                <span className="text-xs text-gray-900">{formatRelativeTime(selectedConversation.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Last activity</span>
                <span className="text-xs text-gray-900">{formatRelativeTime(selectedConversation.last_message_at)}</span>
              </div>
              {conversationTotalCost > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Cost</span>
                  <span className="text-xs text-purple-700 font-medium">{formatCost(conversationTotalCost)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tabbed: Task Pipeline / Documents */}
          <div className="flex-1 flex flex-col overflow-hidden border-b-2 border-[#1a1a2e]">
            <div className="flex border-b-2 border-[#1a1a2e] bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setRightPanelTab('tasks')}
                className={cn(
                  'flex-1 px-3 py-2.5 text-xs font-semibold transition-colors',
                  rightPanelTab === 'tasks'
                    ? 'text-gray-900 bg-white border-b-2 border-rose-500 -mb-[2px]'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                Tasks
              </button>
              <button
                onClick={() => setRightPanelTab('documents')}
                className={cn(
                  'flex-1 px-3 py-2.5 text-xs font-semibold transition-colors',
                  rightPanelTab === 'documents'
                    ? 'text-gray-900 bg-white border-b-2 border-emerald-500 -mb-[2px]'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                Documents
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {rightPanelTab === 'tasks' ? (
                <TaskProgressPanel conversationId={selectedConversation.id} />
              ) : (
                <DocumentsPanel conversationId={selectedConversation.id} />
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="p-4 space-y-2 flex-shrink-0">
            <button
              onClick={handleTakeOver}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                selectedConversation.status === 'escalated'
                  ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
              )}
            >
              {selectedConversation.status === 'escalated' ? (
                <>
                  <Bot className="w-4 h-4" />
                  Hand Back to AI
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4" />
                  Take Over
                </>
              )}
            </button>
            <button
              onClick={handleCloseConversation}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                selectedConversation.status === 'resolved' || (selectedConversation.status as string) === 'closed'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                  : 'bg-gray-50 text-gray-700 border-2 border-[#1a1a2e] hover:bg-gray-100'
              )}
            >
              {selectedConversation.status === 'resolved' || (selectedConversation.status as string) === 'closed' ? (
                <>
                  <MessageSquare className="w-4 h-4" />
                  Reopen Conversation
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Close Conversation
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ==================== Customer Profile Modal ==================== */}
      {customerProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setCustomerProfile(null); setCustomerAttachments([]) }}>
          <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-lg mx-4 p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Customer Profile</h3>
              <button onClick={() => { setCustomerProfile(null); setCustomerAttachments([]) }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-rose-500 border-2 border-[#1a1a2e] flex items-center justify-center text-white text-xl font-bold">
                  {(customerProfile.customer?.name || customerProfile.customer?.external_id || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{customerProfile.customer?.name || 'Unknown'}</p>
                  {customerProfile.customer?.email && <p className="text-sm text-gray-500">{customerProfile.customer.email}</p>}
                  {customerProfile.customer?.phone && <p className="text-sm text-gray-500">{customerProfile.customer.phone}</p>}
                  {customerProfile.customer?.external_id && <p className="text-xs text-gray-400">ID: {customerProfile.customer.external_id}</p>}
                </div>
              </div>

              {/* Customer tag buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    const isHighPotential = !customerProfile.customer?.metadata?.is_high_potential
                    try {
                      await conversationsService.updateCustomerTags(customerProfile.customer.id, { is_high_potential: isHighPotential })
                      setCustomerProfile((prev: any) => ({
                        ...prev,
                        customer: {
                          ...prev.customer,
                          metadata: { ...prev.customer.metadata, is_high_potential: isHighPotential, high_potential_tagged_at: isHighPotential ? new Date().toISOString() : null },
                        },
                      }))
                      fetchConversations(pagination.currentPage)
                      toast.success(isHighPotential ? 'Marked as high potential' : 'High potential tag removed')
                    } catch { toast.error('Failed to update tag') }
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    customerProfile.customer?.metadata?.is_high_potential
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600 border-2 border-[#1a1a2e]'
                  )}
                >
                  <Star className="w-4 h-4" />
                  High Potential
                </button>
                <button
                  onClick={async () => {
                    const isBlocked = !customerProfile.customer?.metadata?.is_blocked
                    try {
                      await conversationsService.updateCustomerTags(customerProfile.customer.id, { is_blocked: isBlocked })
                      setCustomerProfile((prev: any) => ({
                        ...prev,
                        customer: {
                          ...prev.customer,
                          metadata: { ...prev.customer.metadata, is_blocked: isBlocked, blocked_at: isBlocked ? new Date().toISOString() : null },
                        },
                      }))
                      fetchConversations(pagination.currentPage)
                      toast.success(isBlocked ? 'Customer blocked' : 'Customer unblocked')
                    } catch { toast.error('Failed to update tag') }
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    customerProfile.customer?.metadata?.is_blocked
                      ? 'bg-red-100 text-red-700 border border-red-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 border-2 border-[#1a1a2e]'
                  )}
                >
                  <Ban className="w-4 h-4" />
                  {customerProfile.customer?.metadata?.is_blocked ? 'Blocked' : 'Block'}
                </button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Conversations: {customerProfile.conversations_count}</p>
                {customerProfile.total_cost && customerProfile.total_cost.total_cost_usd > 0 && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Total Spend</span>
                      <span className="text-sm font-bold text-purple-900">{formatCost(customerProfile.total_cost.total_cost_usd)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-purple-400">Input</span>
                        <p className="text-purple-800 font-semibold">{customerProfile.total_cost.input_tokens?.toLocaleString() || 0} tok</p>
                      </div>
                      <div>
                        <span className="text-purple-400">Output</span>
                        <p className="text-purple-800 font-semibold">{customerProfile.total_cost.output_tokens?.toLocaleString() || 0} tok</p>
                      </div>
                      <div>
                        <span className="text-purple-400">Requests</span>
                        <p className="text-purple-800 font-semibold">{customerProfile.total_cost.request_count}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {Object.keys(customerProfile.aggregated_data || {}).length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Collected Data</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    {Object.entries(customerProfile.aggregated_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-gray-500">{key}:</span>
                        <span className="text-gray-900 font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {customerFilesSection}

              {customerProfile.timeline && customerProfile.timeline.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Activity Timeline</p>
                  <div className="space-y-2">
                    {customerProfile.timeline.map((item: any) => (
                      <div key={item.conversation_id} className="flex items-center gap-3 text-sm">
                        <div className={cn('w-2 h-2 rounded-full', item.status === 'active' ? 'bg-green-500' : item.status === 'escalated' ? 'bg-red-500' : 'bg-gray-400')} />
                        <span className="text-gray-600">{item.channel_type}</span>
                        <span className="text-gray-400">-</span>
                        <span className={cn('capitalize', item.status === 'active' ? 'text-green-600' : item.status === 'escalated' ? 'text-red-600' : 'text-gray-600')}>{item.status}</span>
                        <span className="text-gray-400 ml-auto text-xs">{new Date(item.started_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {customerTaskProgress.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Task Pipeline Progress</p>
                  <div className="space-y-2">
                    {customerTaskProgress.map((tp) => (
                      <div key={tp.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{tp.task_name || 'Task'}</span>
                          <span className={cn(
                            'text-xs font-medium px-2 py-0.5 rounded-full',
                            tp.status === 'completed' ? 'bg-green-100 text-green-700'
                              : tp.status === 'in_progress' ? 'bg-blue-100 text-blue-700'
                              : tp.status === 'blocked' ? 'bg-orange-100 text-orange-700'
                              : tp.status === 'skipped' ? 'bg-gray-100 text-gray-500'
                              : 'bg-gray-50 text-gray-400'
                          )}>{tp.status}</span>
                        </div>
                        {tp.task_type === 'data_collection' && Object.keys(tp.collected_data || {}).length > 0 && (
                          <div className="space-y-0.5 mt-1">
                            {Object.entries(tp.collected_data).map(([k, v]) => (
                              <div key={k} className="flex justify-between text-xs">
                                <span className="text-gray-500">{k}</span>
                                <span className="text-gray-900">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {tp.task_type === 'document_creation' && tp.generated_content && (
                          <div className="mt-1">
                            <p className="text-xs text-gray-500 truncate">{tp.generated_content.substring(0, 100)}...</p>
                            {tp.approved_at && <span className="text-xs text-green-600 font-medium">Approved</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConversationsPage
